import {
  Button,
  ButtonIcon,
  Checkbox,
  Modal,
  Selection,
  Typography,
  useMobile,
} from '@sonnen/web-ui';
import { useEffect, useState } from 'react';
import { useAppContext } from '../../../AppContext/AppContext';
import step1 from '../../../assets/images/HeatPumpStep1.webp';
import step2 from '../../../assets/images/HeatPumpStep2.webp';
import { SonnenHomeEnergyLogo } from '../../../components/SonnenHomeEnergyLogo/SonnenHomeEnergyLogo';
import { useOrderFlowNavigation } from '../../../hooks/useOrderFlowNavigation';
import { useTranslation } from '../../../i18n/i18n';
import styles from './SelfConsumptionPlusModal.module.css';

export const SelfConsumptionPlusModal = () => {
  const { t } = useTranslation();
  const { setCurrentModal } = useAppContext();
  const [checked, setChecked] = useState(false);
  const [view, setView] = useState<'initial' | 'details'>('initial');
  const { navigateNext } = useOrderFlowNavigation();
  const mobile = useMobile();

  const initialView = view === 'initial';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onClose = () => setCurrentModal(undefined);

  const onContinueClick = () => {
    if (initialView && checked && navigateNext) {
      navigateNext();
      onClose();
      return;
    }
    setView('initial');
  };

  const onBackButtonClick = () => {
    if (mobile && initialView) {
      onClose();
    } else {
      setView('initial');
    }
  };

  const title = initialView ? 'title1' : 'title2';

  return (
    <Modal dataTestId="self-consumption-modal">
      <div className={styles.modal}>
        {mobile && (
          <div className={styles.logoWrapper}>
            <SonnenHomeEnergyLogo />
          </div>
        )}
        <div className={`${styles.contentWrapper} ${view === 'details' && styles.detailsView}`}>
          <div className={styles.titleRow}>
            {(view === 'details' || mobile) && (
              <ButtonIcon
                ariaLabel="back icon"
                dataTestId="back-icon"
                icon="ArrowLeft"
                iconColor="primary"
                size="lg"
                tooltipContent="back"
                onClick={onBackButtonClick}
              />
            )}
            <Typography.H3 data-testid={title}>
              {t(`selfConsumptionPlus.modal.${title}`)}
            </Typography.H3>
            {initialView && !mobile && (
              <ButtonIcon
                ariaLabel="Close"
                icon="Close"
                onClick={onClose}
                dataTestId="close-icon"
                iconColor="primary"
                size="xl"
              />
            )}
          </div>
          <Typography.Body1>
            {t(`selfConsumptionPlus.modal.${initialView ? 'subtitle1' : 'step1'}`)}
          </Typography.Body1>
          {initialView && (
            <>
              <Typography.Body1>{t(`selfConsumptionPlus.modal.subtitle1`)}</Typography.Body1>

              <Selection
                state="default"
                label={t('selfConsumptionPlus.modal.settingsButton')}
                leadingIcon="Notes"
                trailingIcon="ChevronRight"
                shape="horizontal"
                onClick={() => setView('details')}
              />

              <Checkbox
                name="heatpump-confirmation"
                dataTestId="heatpump-checkbox"
                checked={checked}
                clickableLabel
                onChange={() => setChecked(!checked)}
              >
                <Typography.Body1>{t('selfConsumptionPlus.modal.checkbox')}</Typography.Body1>
              </Checkbox>

              <Typography.Body2>{t('selfConsumptionPlus.modal.note')}</Typography.Body2>
            </>
          )}
          {view === 'details' && (
            <>
              <img
                data-testid="configuration-image-step1"
                src={step1}
                alt={t('selfConsumptionPlus.modal.imageLabelStep1')}
                aria-label={t('selfConsumptionPlus.modal.imageLabelStep1')}
              />
              <Typography.Body1>{t(`selfConsumptionPlus.modal.step2`)}</Typography.Body1>
              <img
                data-testid="configuration-image-step2"
                src={step2}
                alt={t('selfConsumptionPlus.modal.imageLabelStep2')}
                aria-label={t('selfConsumptionPlus.modal.imageLabelStep2')}
              />
            </>
          )}
        </div>
        <div className={styles.stickyFooter}>
          <Button
            dataTestId={initialView ? 'continue-button' : 'understood-button'}
            label={t(
              `selfConsumptionPlus.modal.${initialView ? 'continueButton' : 'understoodButton'}`,
            )}
            onClick={onContinueClick}
            disabled={initialView && !checked}
            expanded
            size="large"
          />
        </div>
      </div>
    </Modal>
  );
};
